import React, { useState, useEffect } from 'react';
import {
  Table, Tag, Card, Row, Col, Spin, Alert, Empty, Button, Select, Input, Typography, 
  Space, Avatar, Badge, Tooltip, Collapse, List, Divider, Statistic, Radio
} from 'antd';
import {
  TransactionOutlined, UserOutlined, SearchOutlined, FilterOutlined, 
  ReloadOutlined, BarChartOutlined, ClockCircleOutlined, DollarOutlined,
  CheckCircleOutlined, CloseCircleOutlined, WalletOutlined, LineChartOutlined,
  DownOutlined, RightOutlined, CaretRightOutlined
} from '@ant-design/icons';
import 'antd/dist/reset.css';
import Chart from 'react-apexcharts';
import { Link } from 'react-router-dom';

const { Title, Text } = Typography;
const { Option } = Select;
const { Panel } = Collapse;

const TransactionsPage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [groupedTransactions, setGroupedTransactions] = useState([]);

  // Metrics
  const [metrics, setMetrics] = useState({
    totalTransactions: 0,
    totalCoinsUsed: 0,
    avgCoinsPerTransaction: 0,
    successfulTransactions: 0,
    failedTransactions: 0
  });

  // Chart states
  const [transactionsByType, setTransactionsByType] = useState({
    labels: [],
    series: []
  });

  const [transactionsByDay, setTransactionsByDay] = useState({
    categories: [],
    series: [{
      name: 'Transactions',
      data: []
    }]
  });

  // Add time scale state
  const [timeScale, setTimeScale] = useState('daily'); // 'daily', 'weekly', 'monthly'

  useEffect(() => {
    fetchTransactions();
  }, []);

  useEffect(() => {
    if (transactions.length > 0) {
      calculateMetrics();
      prepareChartData();
      groupTransactionsByUser();
    }
  }, [transactions, searchText, filterStatus, filterType, timeScale]);

  // Add a utility function for retrying API calls
  const fetchWithRetry = async (url, options = {}, retries = 2, delay = 1000) => {
    try {
      const response = await fetch(url, options);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      if (retries <= 0) {
        throw error;
      }
      console.log(`Retrying fetch to ${url}. Attempts remaining: ${retries}`);
      await new Promise(resolve => setTimeout(resolve, delay));
      return fetchWithRetry(url, options, retries - 1, delay);
    }
  };

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const data = await fetchWithRetry('https://main-matrixai-server-lujmidrakh.cn-hangzhou.fcapp.run/api/admin/getAllTransactions', {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (data.success && Array.isArray(data.data)) {
        // Process transactions
        const processedTransactions = processTransactionsData(data.data);
        setTransactions(processedTransactions);
      } else if (data.data && Array.isArray(data.data)) {
        // Handle case where success flag might be missing but data is present
        const processedTransactions = processTransactionsData(data.data);
        setTransactions(processedTransactions);
      } else if (Array.isArray(data)) {
        // Handle case where the API directly returns an array
        const processedTransactions = processTransactionsData(data);
        setTransactions(processedTransactions);
      } else {
        console.error('Invalid transactions data format:', data);
        setTransactions([]);
      }
    } catch (err) {
      console.error('Error fetching transactions:', err);
      setError(err.message);
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  };

  const processTransactionsData = (usersData) => {
    let allTransactions = [];
    
    usersData.forEach(userData => {
      const { user, transactions } = userData;
      
      if (transactions && transactions.length > 0) {
        transactions.forEach(transaction => {
          allTransactions.push({
            ...transaction,
            user_name: user.name || 'Unknown User',
            user_email: user.email || 'No email',
            user_uid: user.uid,
            user_avatar: user.dp_url,
            key: `${transaction.id}-${user.uid}`
          });
        });
      }
    });
    
    return allTransactions;
  };

  const calculateMetrics = () => {
    if (transactions.length === 0) return;

    const totalTransactions = transactions.length;
    const totalCoinsUsed = transactions.reduce((sum, txn) => sum + txn.coin_amount, 0);
    const avgCoinsPerTransaction = totalCoinsUsed / totalTransactions;
    const successfulTransactions = transactions.filter(txn => txn.status === 'success').length;
    const failedTransactions = totalTransactions - successfulTransactions;

    setMetrics({
      totalTransactions,
      totalCoinsUsed,
      avgCoinsPerTransaction,
      successfulTransactions,
      failedTransactions
    });
  };

  const prepareChartData = () => {
    if (transactions.length === 0) return;

    // Transactions by type
    const transactionTypes = {};
    transactions.forEach(txn => {
      const type = txn.transaction_name || 'Unknown';
      if (transactionTypes[type]) {
        transactionTypes[type]++;
      } else {
        transactionTypes[type] = 1;
      }
    });

    setTransactionsByType({
      labels: Object.keys(transactionTypes),
      series: Object.values(transactionTypes)
    });

    // Time scale calculations
    let timeScalePeriod, startDate;
    const now = new Date();
    
    switch(timeScale) {
      case 'daily':
        timeScalePeriod = 30; // Last 30 days
        startDate = new Date(now);
        startDate.setDate(now.getDate() - timeScalePeriod);
        break;
      case 'weekly':
        timeScalePeriod = 12; // Last 12 weeks
        startDate = new Date(now);
        startDate.setDate(now.getDate() - (timeScalePeriod * 7));
        break;
      case 'monthly':
      default:
        timeScalePeriod = 6; // Last 6 months
        startDate = new Date(now);
        startDate.setMonth(now.getMonth() - timeScalePeriod);
        break;
    }

    // Initialize time periods
    const timePeriods = {};
    
    if (timeScale === 'daily') {
      // Initialize last X days
      for (let i = 0; i < timeScalePeriod; i++) {
        const d = new Date(startDate);
        d.setDate(startDate.getDate() + i);
        const dateStr = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        timePeriods[dateStr] = 0;
      }
    } else if (timeScale === 'weekly') {
      // Initialize last X weeks
      for (let i = 0; i < timeScalePeriod; i++) {
        const d = new Date(startDate);
        d.setDate(startDate.getDate() + (i * 7));
        const weekNumber = Math.ceil((d.getDate() + d.getDay()) / 7);
        const dateStr = `Week ${weekNumber}`;
        timePeriods[dateStr] = 0;
      }
    } else {
      // Monthly (default)
      for (let i = 0; i < timeScalePeriod; i++) {
        const d = new Date(startDate);
        d.setMonth(startDate.getMonth() + i);
        const monthYear = `${d.toLocaleString('default', { month: 'short' })} ${d.getFullYear()}`;
        timePeriods[monthYear] = 0;
      }
    }

    // Count transactions by time period
    transactions.forEach(txn => {
      try {
        const txnDate = new Date(txn.time);
        if (txnDate >= startDate) {
          let dateStr;
          
          if (timeScale === 'daily') {
            dateStr = txnDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
          } else if (timeScale === 'weekly') {
            const weekNumber = Math.ceil((txnDate.getDate() + txnDate.getDay()) / 7);
            dateStr = `Week ${weekNumber}`;
          } else {
            dateStr = `${txnDate.toLocaleString('default', { month: 'short' })} ${txnDate.getFullYear()}`;
          }
          
          if (timePeriods[dateStr] !== undefined) {
            timePeriods[dateStr]++;
          }
        }
      } catch (err) {
        console.error('Error processing transaction date:', err);
      }
    });

    setTransactionsByDay({
      categories: Object.keys(timePeriods),
      series: [{
        name: 'Transactions',
        data: Object.values(timePeriods)
      }]
    });
  };

  const groupTransactionsByUser = () => {
    let result = [...transactions];
    
    // Apply search filter
    if (searchText) {
      const lowerSearchText = searchText.toLowerCase();
      result = result.filter(txn => 
        (txn.user_name && txn.user_name.toLowerCase().includes(lowerSearchText)) ||
        (txn.user_email && txn.user_email.toLowerCase().includes(lowerSearchText)) ||
        (txn.transaction_name && txn.transaction_name.toLowerCase().includes(lowerSearchText))
      );
    }
    
    // Apply status filter
    if (filterStatus !== 'all') {
      result = result.filter(txn => txn.status === filterStatus);
    }
    
    // Apply type filter
    if (filterType !== 'all') {
      result = result.filter(txn => txn.transaction_name === filterType);
    }
    
    // Group by user
    const grouped = result.reduce((acc, transaction) => {
      const userId = transaction.user_uid;
      if (!acc[userId]) {
        acc[userId] = {
          user: {
            uid: userId,
            name: transaction.user_name,
            email: transaction.user_email,
            avatar: transaction.user_avatar
          },
          transactions: []
        };
      }
      acc[userId].transactions.push(transaction);
      return acc;
    }, {});
    
    // Sort transactions by date (newest first) within each user
    Object.values(grouped).forEach(group => {
      group.transactions.sort((a, b) => new Date(b.time) - new Date(a.time));
    });
    
    // Convert to array and sort by most recent transaction
    const groupedArray = Object.values(grouped).sort((a, b) => {
      const latestA = a.transactions[0]?.time || 0;
      const latestB = b.transactions[0]?.time || 0;
      return new Date(latestB) - new Date(latestA);
    });
    
    setGroupedTransactions(groupedArray);
  };

  const handleSearch = (e) => {
    setSearchText(e.target.value);
  };

  const handleStatusFilterChange = (value) => {
    setFilterStatus(value);
  };

  const handleTypeFilterChange = (value) => {
    setFilterType(value);
  };

  const resetFilters = () => {
    setSearchText('');
    setFilterStatus('all');
    setFilterType('all');
  };

  const getTransactionTypes = () => {
    const types = new Set(transactions.map(txn => txn.transaction_name).filter(Boolean));
    return ['all', ...types];
  };

  // Chart options
  const donutOptions = {
    chart: {
      type: 'donut',
      fontFamily: 'Inter, sans-serif',
      toolbar: {
        show: false
      }
    },
    legend: {
      position: 'bottom'
    },
    dataLabels: {
      enabled: false
    },
    colors: ['var(--primary-color)', 'var(--secondary-color)', 'var(--accent-color)', 'var(--warning-color)'],
    plotOptions: {
      pie: {
        donut: {
          size: '60%',
          labels: {
            show: true,
            name: {
              show: true
            },
            value: {
              show: true,
              fontSize: '22px',
              fontWeight: 600
            },
            total: {
              show: true,
              label: 'Total',
              formatter: function (w) {
                return w.globals.seriesTotals.reduce((a, b) => a + b, 0);
              }
            }
          }
        }
      }
    }
  };

  const lineOptions = {
    chart: {
      type: 'line',
      fontFamily: 'Inter, sans-serif',
      toolbar: {
        show: false
      },
      zoom: {
        enabled: false
      }
    },
    dataLabels: {
      enabled: false
    },
    stroke: {
      curve: 'smooth',
      width: 3
    },
    colors: ['var(--primary-color)'],
    xaxis: {
      categories: transactionsByDay.categories
    },
    yaxis: {
      title: {
        text: 'Transactions'
      }
    },
    tooltip: {
      x: {
        format: 'dd MMM yyyy'
      }
    },
    fill: {
      type: 'gradient',
      gradient: {
        shade: 'light',
        type: "vertical",
        shadeIntensity: 0.25,
        gradientToColors: undefined,
        inverseColors: true,
        opacityFrom: 1,
        opacityTo: 0.7,
        stops: [0, 100]
      }
    }
  };

  const renderTransactionItem = (transaction) => {
    const statusColor = transaction.status === 'success' ? 'success' : 'error';
    const statusIcon = transaction.status === 'success' ? <CheckCircleOutlined /> : <CloseCircleOutlined />;
    
    const formattedDate = new Date(transaction.time).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
    
    const formattedTime = new Date(transaction.time).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
    
    return (
      <List.Item 
        key={transaction.id}
        className="transaction-item"
        style={{
          padding: '16px',
          borderRadius: '8px',
          backgroundColor: 'var(--background-secondary)',
          marginBottom: '8px',
          transition: 'all 0.3s ease',
          boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
          border: '1px solid var(--border-color)'
        }}
        actions={[
          <Tag color={statusColor} icon={statusIcon} style={{ borderRadius: '12px', padding: '2px 8px' }}>
            {transaction.status}
          </Tag>
        ]}
      >
        <div className="transaction-content" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          <div className="transaction-main" style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div className="transaction-icon">
              <Avatar 
                icon={<TransactionOutlined />} 
                style={{ 
                  backgroundColor: 'var(--primary-color)',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)' 
                }} 
                size="large"
              />
            </div>
            <div className="transaction-details" style={{ display: 'flex', flexDirection: 'column' }}>
              <div className="transaction-title" style={{ fontWeight: '600', fontSize: '16px', color: 'var(--text-primary)' }}>
                {transaction.transaction_name || 'Unknown Transaction'}
              </div>
              <div className="transaction-subtitle" style={{ color: 'var(--text-tertiary)', fontSize: '14px' }}>
                ID: {transaction.id} • {formattedDate} at {formattedTime}
              </div>
            </div>
          </div>
          <div className="transaction-amount" style={{ textAlign: 'right' }}>
            <div className="coin-amount" style={{ 
              fontWeight: '700', 
              fontSize: '18px',
              color: 'var(--primary-color)',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}>
              {transaction.coin_amount} <span style={{ fontSize: '14px', fontWeight: '500', opacity: '0.8' }}>coins</span>
            </div>
          </div>
        </div>
      </List.Item>
    );
  };

  const renderUserPanel = (userGroup, index) => {
    const { user, transactions } = userGroup;
    const totalTransactions = transactions.length;
    const successfulTransactions = transactions.filter(t => t.status === 'success').length;
    const totalCoins = transactions.reduce((sum, t) => sum + t.coin_amount, 0);
    
    return (
      <Panel 
        key={user.uid || index}
        header={
          <div className="user-panel-header">
            <div className="user-info">
              <Avatar 
                src={user.avatar} 
                icon={!user.avatar && <UserOutlined />} 
                size="large"
                style={{ backgroundColor: 'var(--primary-color)' }}
              />
              <div className="user-details">
                <div className="user-name">{user.name || 'Unknown User'}</div>
                <div className="user-email">{user.email || 'No email'}</div>
              </div>
            </div>
            <div className="user-stats">
              <div className="stat-item">
                <span className="stat-label">Transactions:</span>
                <span className="stat-value">{totalTransactions}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Success Rate:</span>
                <span className="stat-value">
                  {totalTransactions > 0 ? Math.round((successfulTransactions / totalTransactions) * 100) : 0}%
                </span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Total Coins:</span>
                <span className="stat-value">{totalCoins}</span>
              </div>
            </div>
          </div>
        }
      >
        <List
          className="transaction-list"
          itemLayout="horizontal"
          dataSource={transactions}
          renderItem={renderTransactionItem}
          pagination={{
            pageSize: 5,
            size: 'small',
            showSizeChanger: false
          }}
        />
        <div className="user-actions">
          <Link to={`/users?uid=${user.uid}`}>
            <Button type="primary" ghost>
              View User Profile
            </Button>
          </Link>
        </div>
      </Panel>
    );
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', padding: '2rem' }}>
        <Spin size="large" tip="Loading transaction data..." spinning={true} />
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="Error Loading Data"
        description={error}
        type="error"
        showIcon
        style={{ margin: '2rem' }}
      />
    );
  }

  return (
    <div style={{ padding: '24px', background: 'var(--background-primary)', minHeight: '100vh' }}>
      <div style={{ marginBottom: '24px' }}>
        <Title level={3} style={{ color: 'var(--primary-color)', margin: 0 }}>
          <WalletOutlined /> Transaction Management
        </Title>
        <Text type="secondary">
          Overview and analysis of all user transactions
        </Text>
      </div>

      {/* Stats Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} lg={6}>
          <div className="stat-card primary">
            <div>
              <div className="stat-value">{metrics.totalTransactions}</div>
              <div className="stat-label">Total Transactions</div>
            </div>
            <TransactionOutlined className="stat-icon" />
          </div>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <div className="stat-card secondary">
            <div>
              <div className="stat-value">{metrics.totalCoinsUsed}</div>
              <div className="stat-label">Total Coins Used</div>
            </div>
            <WalletOutlined className="stat-icon" />
          </div>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <div className="stat-card accent">
            <div>
              <div className="stat-value">{metrics.avgCoinsPerTransaction.toFixed(1)}</div>
              <div className="stat-label">Avg. Coins Per Transaction</div>
            </div>
            <BarChartOutlined className="stat-icon" />
          </div>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <div className="stat-card warning">
            <div>
              <div className="stat-value">{metrics.successfulTransactions}</div>
              <div className="stat-label">Successful Transactions</div>
            </div>
            <CheckCircleOutlined className="stat-icon" />
          </div>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} lg={12}>
          <Card 
            title="Transactions Trend" 
            className="dashboard-card"
            bodyStyle={{ height: '300px' }}
            extra={
              <Radio.Group 
                value={timeScale}
                onChange={e => setTimeScale(e.target.value)}
                size="small"
                buttonStyle="solid"
              >
                <Radio.Button value="daily">Daily</Radio.Button>
                <Radio.Button value="weekly">Weekly</Radio.Button>
                <Radio.Button value="monthly">Monthly</Radio.Button>
              </Radio.Group>
            }
          >
            <div style={{ height: '280px', width: '100%' }}>
              {transactionsByDay.series[0].data.length > 0 ? (
                <Chart
                  options={{
                    ...lineOptions,
                    xaxis: {
                      ...lineOptions.xaxis,
                      categories: transactionsByDay.categories,
                      labels: {
                        rotate: -45,
                        style: { fontSize: '12px' }
                      }
                    },
                    grid: {
                      padding: {
                        left: 10,
                        right: 10
                      }
                    }
                  }}
                  series={transactionsByDay.series}
                  type="line"
                  height="280"
                  width="100%"
                />
              ) : (
                <Empty description="No transaction data available" style={{ marginTop: '80px' }} />
              )}
            </div>
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card 
            title="Transaction Types Distribution" 
            className="dashboard-card"
            bodyStyle={{ height: '300px' }}
          >
            <div style={{ height: '280px', width: '100%' }}>
              {transactionsByType.series.some(value => value > 0) ? (
                <Chart
                  options={{
                    ...donutOptions,
                    labels: transactionsByType.labels,
                    chart: {
                      ...donutOptions.chart,
                      width: '100%'
                    }
                  }}
                  series={transactionsByType.series}
                  type="donut"
                  height="280"
                  width="100%"
                />
              ) : (
                <Empty description="No transaction data available" style={{ marginTop: '80px' }} />
              )}
            </div>
          </Card>
        </Col>
      </Row>

      <Card className="dashboard-card" style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', marginBottom: '16px', gap: '16px', flexWrap: 'wrap' }}>
          <Input
            placeholder="Search by name, email or transaction type"
            value={searchText}
            onChange={handleSearch}
            style={{ maxWidth: '300px' }}
            prefix={<SearchOutlined />}
            allowClear
          />
          
          <Space wrap>
            <Select
              value={filterStatus}
              onChange={handleStatusFilterChange}
              style={{ width: '160px' }}
              placeholder="Filter by status"
            >
              <Option value="all">All Statuses</Option>
              <Option value="success">Success</Option>
              <Option value="failed">Failed</Option>
            </Select>
            
            <Select
              value={filterType}
              onChange={handleTypeFilterChange}
              style={{ width: '200px' }}
              placeholder="Filter by transaction type"
            >
              {getTransactionTypes().map(type => (
                <Option key={type} value={type}>
                  {type === 'all' ? 'All Types' : type}
                </Option>
              ))}
            </Select>
            
            <Button 
              icon={<ReloadOutlined />} 
              onClick={resetFilters}
              type="text"
              title="Reset filters"
            >
              Reset
            </Button>
          </Space>
          
          <div style={{ marginLeft: 'auto' }}>
            <Button 
              type="primary" 
              icon={<ReloadOutlined />}
              onClick={fetchTransactions}
            >
              Refresh
            </Button>
          </div>
        </div>

        {groupedTransactions.length > 0 ? (
          <Collapse 
            className="user-transactions-collapse" 
            expandIcon={({ isActive }) => isActive ? <CaretRightOutlined rotate={90} /> : <CaretRightOutlined />}
          >
            {groupedTransactions.map((group, index) => renderUserPanel(group, index))}
          </Collapse>
        ) : (
          <Empty description="No transactions found matching your filters" />
        )}
      </Card>
    </div>
  );
};

export default TransactionsPage;